'use client';

import type { ElementType } from 'react';
import { createElement } from 'react';

import './TextType.css';
import { useTextAnimation } from './useTextAnimation';

interface TextTypeProps {
  className?: string;
  showCursor?: boolean;
  hideCursorWhileTyping?: boolean;
  cursorCharacter?: string | React.ReactNode;
  cursorBlinkDuration?: number;
  cursorClassName?: string;
  text: string | string[];
  as?: ElementType;
  typingSpeed?: number;
  initialDelay?: number;
  pauseDuration?: number;
  deletingSpeed?: number;
  loop?: boolean;
  textColors?: string[];
  variableSpeed?: { min: number; max: number };
  onSentenceComplete?: (sentence: string, index: number) => void;
  onComplete?: () => void;
  startOnVisible?: boolean;
  reverseMode?: boolean;
}

const TextType = ({
  text,
  as: Component = 'div',
  typingSpeed = 40,
  initialDelay = 0,
  pauseDuration = 2000,
  deletingSpeed = 30,
  loop = true,
  className = '',
  showCursor = true,
  hideCursorWhileTyping = false,
  cursorCharacter = '|',
  cursorClassName = '',
  cursorBlinkDuration = 0.5,
  textColors = [],
  variableSpeed,
  onSentenceComplete,
  onComplete,
  startOnVisible = false,
  reverseMode = false,
  ...props
}: TextTypeProps & React.HTMLAttributes<HTMLElement>) => {
  const {
    displayedText,
    currentTextIndex,
    currentCharIndex,
    isDeleting,
    textArray,
    setContainerElement,
    cursorRef
  } = useTextAnimation({
    text,
    typingSpeed,
    initialDelay,
    pauseDuration,
    deletingSpeed,
    loop,
    variableSpeed,
    showCursor,
    cursorBlinkDuration,
    startOnVisible,
    reverseMode,
    onSentenceComplete,
    onComplete
  });

  const currentColor =
    textColors.length > 0 ? textColors[currentTextIndex % textColors.length] : undefined;

  const shouldHideCursor =
    hideCursorWhileTyping && (currentCharIndex < textArray[currentTextIndex].length || isDeleting);

  const content = (
    <>
      <span
        className='text-type__content'
        style={{ color: currentColor ?? 'inherit' }}
      >
        {displayedText}
      </span>
      {showCursor && (
        <span
          ref={cursorRef}
          className={`text-type__cursor ${cursorClassName} ${shouldHideCursor ? 'text-type__cursor--hidden' : ''}`}
        >
          {cursorCharacter}
        </span>
      )}
    </>
  );

  return createElement(
    Component,
    { ...props, className: `text-type ${className}`, ref: setContainerElement },
    content
  );
};

export default TextType;
